/**
 * Shared types for the saved-view selector. `SavedViewTabs` is the controller
 * (data, mutations, modals); it renders one of two presentations — the
 * desktop pill strip (`SavedViewStrip`) or the mobile dropdown
 * (`SavedViewMenu`) — through the `SavedViewPresentationProps` contract.
 */

import type { ActiveListView, BuiltInViewConfig } from '@/lib/crm/core/types';
import type { SavedFilterView } from '@/types/savedFilterView';

/** Identifies which pill is the user's default view for a list. */
export interface DefaultViewRef {
  kind: 'saved' | 'builtin' | 'all';
  /** Required for 'saved' and 'builtin'; unused for 'all'. */
  id?: string;
}

export function isDefaultRef(
  defaultViewRef: DefaultViewRef | null | undefined,
  ref: DefaultViewRef
): boolean {
  if (!defaultViewRef || defaultViewRef.kind !== ref.kind) {
    return false;
  }
  return ref.kind === 'all' || defaultViewRef.id === ref.id;
}

/**
 * Actions offered on the active saved view while it has unsaved changes.
 * Built once by the controller so the memoized pills receive a stable object.
 */
export interface DirtyViewActions {
  /** Owner / permissions admin: "Save changes"; otherwise "Save as new view". */
  canSave: boolean;
  isSaving: boolean;
  onSave?: () => void;
  onSaveAsNew: () => void;
  onDiscard?: () => void;
  /**
   * Platform hint for the save shortcut, e.g. "⌘S" or "Ctrl+S". Omitted by
   * the mobile presentation, where a keyboard chord is meaningless.
   */
  shortcutHint?: string;
}

/**
 * One selectable entry of the view strip, in strip order: the "All" view (or
 * each built-in view in its place), then every saved view. The strip measures
 * these to decide which stay inline and which collapse into "More".
 */
export type StripItem =
  | { key: string; kind: 'all' }
  | { key: string; kind: 'builtin'; view: BuiltInViewConfig }
  | { key: string; kind: 'saved'; view: SavedFilterView };

/**
 * Everything a presentation needs, derived by the `SavedViewTabs` controller.
 * Every callback is stable (useCallback) per the stable-references rule.
 */
export interface SavedViewPresentationProps {
  activeViewId: string | null;
  /** Accessible views (own + team-shared), oldest first. */
  orderedViews: SavedFilterView[];
  /** When non-null, built-in view tabs replace the "All" view. */
  builtInViews: BuiltInViewConfig[] | null;
  activeBuiltInViewId: string | null;
  onSelectBuiltInView?: (viewId: string) => void;
  onSelectView: (viewId: string) => void;
  onSelectAll: () => void;
  onSaveCurrentView: () => void;
  defaultViewRef: DefaultViewRef | null;
  /** True when the parent enabled the default-view feature. */
  hasDefaultViewFeature: boolean;
  onToggleDefaultAll: () => void;
  onToggleDefaultBuiltIn: (viewId: string) => void;
  onToggleDefaultSaved: (view: SavedFilterView) => void;
  onRename: (view: SavedFilterView) => void;
  onToggleShare: (view: SavedFilterView) => void;
  onDelete: (view: SavedFilterView) => void;
  isMutationPending: boolean;
  /** True when the active saved view has unsaved local changes. */
  viewDirty: boolean;
  dirtyActions: DirtyViewActions;
  /** Whether the current user may create a new saved view ("New view"). */
  canCreateView: boolean;
  /**
   * Builds the canonical `?view=` href for a pill/menu entry, preserving
   * `?mode=`.
   */
  hrefForView: (view: ActiveListView | null) => string;
}
